<script setup lang="ts">
/**
 * Le détail d'une alarme (écran 7, `rendreEpisode` S:7675-7799), en lecture.
 *
 * Ce que l'utilisateur doit vivre : il voit la courbe autour du dépassement,
 * le seuil, depuis quand, qui a été prévenu et par quel canal avec quel
 * résultat, qui a acquitté, et le journal qui dit pourquoi un message n'est
 * pas parti. Une donnée absente se dit « — », jamais 0 ni « aucune » ; une
 * personne que la réponse ne nomme pas reste « une personne de l'équipe ».
 *
 * Ce composant ne lit rien : la page du détail ouvre la lecture (elle clôt
 * une alarme revenue à la normale et non vue, D71) et lui passe la réponse.
 * Lot F6 : « Acquitter » sur une alarme à traiter active (geste 4), et, sur
 * un écart inhabituel, « Cette alerte était-elle utile ? » (geste 40). Rien
 * ne change à l'écran avant la réponse du serveur ; la page relit ensuite.
 */
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import type {
    AlarmesV2Acquittement,
    AlarmesV2Alarme,
    AlarmesV2DetailAlarme,
    AlarmesV2Surveillance,
} from "@modules/alarmes-v2/types/alarmes-v2"
import {
    lireLeJournal,
    lireLesEnvois,
    nomDeLaPersonne,
    type EnvoiLu,
} from "@modules/alarmes-v2/utils/journal"
import {
    formaterDuree,
    formaterInstant,
    formaterMesure,
    formaterSeuil,
} from "@modules/alarmes-v2/utils/mesure"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()
const tr = t as unknown as Traduire

// ================================
// TYPES & INTERFACES
// ================================
/** Une ligne de la liste des faits. */
interface Fait {
    cle: string
    terme: string
    valeur: string
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** La réponse de la route 12. */
        detail: AlarmesV2DetailAlarme
        /** La surveillance de l'alarme, pour l'en-tête, si elle est lue. */
        surveillance?: AlarmesV2Surveillance | null
        /** Le nom du type d'alarme, ou `null`. */
        nomType?: string | null
        /** Relit après un acquittement ; vrai si la relecture a abouti. */
        relire?: () => Promise<boolean>
        /** Pose l'acquittement rendu par le serveur sur le détail. */
        appliquer?: (reponse: AlarmesV2Acquittement) => void
    }>(),
    {
        surveillance: null,
        nomType: null,
        relire: () => Promise.resolve(false),
        appliquer: undefined,
    }
)

// ================================
// COMPUTED
// ================================
const getAlarme = computed(
    (): AlarmesV2Alarme => props.detail.occurrence as AlarmesV2Alarme
)

const getPersonnes = computed(() => props.detail.personnes ?? {})

/** Une information ne s'acquitte pas (D103, D104). */
const getInformation = computed(
    () =>
        getAlarme.value.nature === "pour_information" ||
        getAlarme.value.etat === "declenchee_pour_information"
)

const ABSENT = computed(() => t("alarmesV2.mesure.absente"))

/** Une durée du bilan : `0` se dit « aucune », une absence « — ». */
function duree(s: number | null | undefined): string {
    if (s === 0) return t("alarmesV2.detail.aucune")
    return formaterDuree(s, tr) ?? ABSENT.value
}

/** La liste des faits (S:7697-7724). */
const getFaits = computed((): Fait[] => {
    const a = getAlarme.value
    const bilan = a.bilan ?? null
    const faits: Fait[] = []
    if (a.motif_ouverture === "silence_sonde") {
        const d = formaterDuree(bilan?.duree_non_observee_s, tr)
        const q = formaterInstant(a.derniere_valeur_le, tr)
        faits.push({
            cle: "fait",
            terme: t("alarmesV2.detail.fait"),
            valeur:
                d && q
                    ? t("alarmesV2.detail.defautMesure", { duree: d, quand: q })
                    : t("alarmesV2.detail.defautMesureSeul"),
        })
    } else {
        const v = formaterMesure(
            a.derniere_valeur_constatee,
            a.seuils_figes?.unite,
            tr
        )
        const q = formaterInstant(a.derniere_valeur_le, tr)
        faits.push({
            cle: "mesure",
            terme: t("alarmesV2.detail.derniereMesure"),
            valeur:
                typeof a.derniere_valeur_constatee === "number" && q
                    ? `${v} ${q}`
                    : v,
        })
    }
    const seuil = formaterSeuil(a.seuils_figes, tr)
    faits.push({
        cle: "seuil",
        terme: t("alarmesV2.detail.seuil"),
        valeur: seuil
            ? a.etat_profil_fige?.etat === "ferme"
                ? t("alarmesV2.detail.seuilSiteFerme", { seuil })
                : t("alarmesV2.detail.seuilValeur", { seuil })
            : ABSENT.value,
    })
    faits.push({
        cle: "ouverte",
        terme: getInformation.value
            ? t("alarmesV2.detail.ouverte")
            : t("alarmesV2.detail.activeDepuis"),
        valeur: formaterInstant(a.ouverte_le, tr) ?? ABSENT.value,
    })
    faits.push({
        cle: "hors-seuil",
        terme: t("alarmesV2.detail.dureeHorsSeuil"),
        valeur: duree(bilan?.duree_hors_seuil_s),
    })
    faits.push({
        cle: "sans-mesure",
        terme: t("alarmesV2.detail.dureeSansMesure"),
        valeur: duree(bilan?.duree_non_observee_s),
    })
    const revenue = formaterInstant(a.retour_le, tr)
    if (revenue) {
        faits.push({
            cle: "revenue",
            terme: t("alarmesV2.detail.revenue"),
            valeur: revenue,
        })
    }
    // `prise_en_compte` absent de la réponse : on ne sait pas, jamais « non acquittée ».
    const prise = a.prise_en_compte
    faits.push({
        cle: "acquittement",
        terme: t("alarmesV2.detail.acquittement"),
        valeur: getInformation.value
            ? t("alarmesV2.detail.sansObjet")
            : prise === undefined
              ? ABSENT.value
              : prise
                ? t("alarmesV2.detail.acquittee")
                : t("alarmesV2.detail.nonAcquittee"),
    })
    if (prise && !getInformation.value) {
        faits.push({
            cle: "acquittee-par",
            terme: t("alarmesV2.detail.acquitteePar"),
            valeur: t("alarmesV2.detail.acquitteeParValeur", {
                nom: nomDeLaPersonne(prise.par, getPersonnes.value, tr),
                quand: formaterInstant(prise.le, tr) ?? ABSENT.value,
                canal:
                    prise.canal === "lien"
                        ? t("alarmesV2.detail.canalLien")
                        : prise.canal === "ecran"
                          ? t("alarmesV2.detail.canalEcran")
                          : ABSENT.value,
            }),
        })
    }
    return faits
})

/** Ce que l'ouverture du détail a fait, ou fera (D71, S:7781-7788). */
const getVue = computed((): { titre: string; texte: string } | null => {
    const a = getAlarme.value
    if (a.etat === "retour_a_la_normale_non_vu") {
        return {
            titre: t("alarmesV2.detail.vueTitre"),
            texte: t("alarmesV2.detail.vueTexte"),
        }
    }
    if (a.etat !== "close") return null
    const quand = formaterInstant(a.close_le, tr) ?? ""
    return {
        titre: t("alarmesV2.detail.closeTitre"),
        texte: a.close_par
            ? t("alarmesV2.detail.closeVue", {
                  nom: nomDeLaPersonne(a.close_par, getPersonnes.value, tr),
                  quand,
              })
            : t("alarmesV2.detail.closeLe", { quand }),
    }
})

/** Le contexte du journal et des envois. */
const getContexte = computed(() => ({
    t: tr,
    personnes: getPersonnes.value,
    relancesMax: getAlarme.value.relances?.max ?? null,
    nature: getInformation.value
        ? ("pour_information" as const)
        : ("a_traiter" as const),
}))

/** Le journal, numéroté comme les marqueurs de la courbe. */
const getJournal = computed(() =>
    lireLeJournal(props.detail.journal, getContexte.value)
)

/** Les envois : canal, personne, résultat. */
const getEnvois = computed(() =>
    lireLesEnvois(props.detail.livraisons, getContexte.value)
)

/** Les classes d'un résultat d'envoi. */
const CLASSES_TON: Record<EnvoiLu["ton"], string> = {
    ok: "bg-green-50 text-green-800 border-green-200",
    warn: "bg-amber-50 text-amber-800 border-amber-200",
    danger: "bg-red-50 text-red-800 border-red-200",
    neutre: "bg-surface-50 text-surface-700 border-surface-200",
}

/** L'en-tête : l'équipement, son lieu, le type d'alarme. */
const getSousTitre = computed((): string =>
    [
        props.surveillance?.objet?.site_nom,
        props.nomType ?? t("alarmesV2.liste.typeNonDisponible"),
    ]
        .filter((x): x is string => typeof x === "string" && x !== "")
        .join(" · ")
)
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <article class="flex flex-col gap-5" data-test="detail-alarme">
        <!-- ================================ -->
        <!-- EN-TÊTE -->
        <!-- ================================ -->
        <header>
            <h1 class="text-xl font-semibold text-surface-900">
                {{
                    surveillance?.objet?.nom ||
                    t("alarmesV2.liste.equipementSansNom")
                }}
                <span
                    v-if="getInformation"
                    class="ml-2 rounded border border-sky-200 bg-sky-50 px-2 py-0.5 align-middle text-xs font-medium text-sky-800"
                    >{{ t("alarmesV2.detail.information") }}</span
                >
            </h1>
            <p class="text-sm text-surface-600">{{ getSousTitre }}</p>
            <div class="mt-3">
                <BoutonAcquitter
                    :alarme="getAlarme"
                    :surveillance="surveillance"
                    :relire="relire"
                    :appliquer="appliquer"
                />
            </div>
        </header>

        <!-- ================================ -->
        <!-- LES FAITS -->
        <!-- ================================ -->
        <dl
            class="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-[max-content_1fr]"
            data-test="faits"
        >
            <template v-for="f in getFaits" :key="f.cle">
                <dt class="text-surface-600">{{ f.terme }}</dt>
                <dd class="text-surface-900" :data-test="`fait-${f.cle}`">
                    {{ f.valeur }}
                </dd>
            </template>
        </dl>

        <AvisUtilite :alarme="getAlarme" />

        <div
            v-if="getVue"
            class="rounded border border-surface-200 bg-surface-50 p-3 text-sm"
            data-test="vue"
        >
            <strong class="block text-surface-900">{{ getVue.titre }}</strong>
            <span class="text-surface-700">{{ getVue.texte }}</span>
        </div>

        <!-- ================================ -->
        <!-- LA COURBE -->
        <!-- ================================ -->
        <CourbeAlarme
            :bilan="getAlarme.bilan"
            :seuils="getAlarme.seuils_figes"
            :motif-ouverture="getAlarme.motif_ouverture"
            :evenements="getJournal"
        />

        <!-- ================================ -->
        <!-- QUI A ÉTÉ PRÉVENU -->
        <!-- ================================ -->
        <section class="flex flex-col gap-2" data-test="envois">
            <h2 class="text-sm font-semibold text-surface-900">
                {{ t("alarmesV2.detail.prevenus") }}
            </h2>
            <p
                v-if="getEnvois.length === 0"
                class="text-sm text-surface-600"
                data-test="aucun-envoi"
            >
                {{ t("alarmesV2.detail.aucunEnvoi") }}
            </p>
            <div v-else class="overflow-x-auto">
                <table class="w-full text-left text-sm">
                    <thead class="bg-surface-50 text-xs text-surface-600">
                        <tr>
                            <th scope="col" class="px-3 py-2">
                                {{ t("alarmesV2.detail.colonnes.heure") }}
                            </th>
                            <th scope="col" class="px-3 py-2">
                                {{ t("alarmesV2.detail.colonnes.canal") }}
                            </th>
                            <th scope="col" class="px-3 py-2">
                                {{ t("alarmesV2.detail.colonnes.personne") }}
                            </th>
                            <th scope="col" class="px-3 py-2">
                                {{ t("alarmesV2.detail.colonnes.resultat") }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(e, i) in getEnvois"
                            :key="i"
                            class="border-t border-surface-200"
                            data-test="envoi"
                        >
                            <td class="px-3 py-2 text-surface-700">
                                {{ e.quand }}
                            </td>
                            <td class="px-3 py-2">{{ e.canal }}</td>
                            <td class="px-3 py-2">{{ e.personne }}</td>
                            <td class="px-3 py-2">
                                <span
                                    class="inline-block rounded border px-2 py-0.5 text-xs"
                                    :class="CLASSES_TON[e.ton]"
                                    >{{ e.resultat }}</span
                                >
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>

        <!-- ================================ -->
        <!-- LE JOURNAL -->
        <!-- ================================ -->
        <JournalAlarme
            :lignes="getJournal"
            :incomplet="detail.curseur_suivant !== null"
        />
    </article>
</template>
